import { useAuth } from '@/hooks/useAuth';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { toastOptions } from '@/hooks/useToast';
import StyledContainer from '../styled/StyledContainer';
import StyledFormHeader from '../styled/StyledFormHeader';
import StyledButton from '../styled/StyledButton';
import { CONTACT_EMAIL } from '@/constants/ContactEmail';
import { useMemo, useState } from 'react';
import HorizontalLine from '../ui/HorizontalLine';
import MailIcon from '@/assets/icons/mail-01.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import {
  useDeleteEmployee,
  useDeleteMultipleEmployees,
  useUpdateUserData,
} from '@/api/user.api';
import { useToast } from '@/hooks/useToast';
import { getFileKeyFromUrl } from '@/utils/s3/getFileKeyFromUrl';
import {
  DistributorProfileFormData,
  DistributorProfileSchema,
} from '@/types/profile/DistributorProfileFormData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import PlusIcon from '@/assets/icons/plus.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import DistributorProfileGeneralTabForm from './DistributorProfileGeneralTabForm';
import DistributorProfileEmployeesTab, {
  DistributorProfileEmployeeAdditionModal,
} from './DistributorProfileEmployeesTab';
import ModalDeletion from '../ui/ModalDeletion';
import clsx from 'clsx';
import { LOCALES, useTranslation } from '@/i18n';
import { useLanguage } from '@/contexts/LanguageContext';
import { resolveMarginRateForApiUpdate } from '@/types/common/margin-rate.schema';
import ProfileAccountSelector from './ProfileAccountSelector';
import DistributorProfileExchangeRatesTab from './DistributorProfileExchangeRatesTab';

interface DistributorProfileFormProps {
  userData: {
    id: string;
    firstname: string;
    lastname: string;
    email: string;
    phoneNumber: string;
    password: string;
    agency: {
      name: string;
      logo: string | null;
      address: string;
      city: string;
      postalCode: number;
      country: string;
      website: string;
      marginRate: number | null;
      siren?: string | null;
      networkId: string;
      network: {
        name: string;
        id: string;
      };
    };
  };
  isAdmin: boolean;
}

const DistributorProfileForm = ({
  userData,
  isAdmin,
}: DistributorProfileFormProps) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { mutateAsync: updateUserData } = useUpdateUserData();

  const { currentLanguage } = useLanguage();

  const defaultLanguage = useMemo(
    () => (LOCALES.includes(currentLanguage) ? currentLanguage : LOCALES[0]),
    [currentLanguage],
  );

  const {
    control,
    reset,
    formState: { dirtyFields },
    handleSubmit,
  } = useForm<DistributorProfileFormData>({
    resolver: zodResolver(DistributorProfileSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: userData.firstname,
      lastName: userData.lastname,
      email: userData.email,
      password: undefined,
      phoneNumber: userData.phoneNumber ?? undefined,
      agency: {
        name: userData.agency.name,
        logo: userData.agency.logo ?? undefined,
        address: userData.agency.address,
        city: userData.agency.city,
        postalCode: userData.agency.postalCode,
        country: userData.agency.country
          ? {
              value: userData.agency.country,
              label: userData.agency.country,
            }
          : undefined,
        siren: userData.agency.siren ?? undefined,
        website: userData.agency.website ?? undefined,
        marginRate: userData.agency.marginRate ?? undefined,
      },
      network: userData.agency.network?.id
        ? {
            label: userData.agency.network.name,
            value: userData.agency.network.id,
          }
        : undefined,
      mainLanguage: {
        value: defaultLanguage,
        label: t(`common.languages.${defaultLanguage}`),
      },
    },
  });

  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [currentTab, setCurrentTab] = useState<
    'profile' | 'users' | 'exchangeRates'
  >('profile');
  const [isAdditionModalOpen, setIsAdditionModalOpen] = useState(false);
  const [selectedUsersIds, setSelectedUsersIds] = useState<string[]>([]);
  const [isUserDeletionModalOpen, setIsUserDeletionModalOpen] = useState(false);

  const { toastAsync } = useToast();

  const { mutateAsync: deleteEmployee } = useDeleteEmployee();
  const { mutateAsync: deleteMultipleEmployees } = useDeleteMultipleEmployees();

  const submitForm: SubmitHandler<DistributorProfileFormData> = async (
    data,
  ) => {
    const formData = new FormData();
    if (dirtyFields.agency?.name && data.agency.name)
      formData.append('companyName', data.agency.name);
    if (dirtyFields.agency?.logo && data.agency.logo instanceof Blob) {
      formData.append('file', data.agency.logo);
    } else if (
      dirtyFields.agency?.logo &&
      data.agency?.logo != null &&
      typeof data.agency.logo === 'string'
    ) {
      formData.append(
        'imageKey',
        getFileKeyFromUrl(data.agency.logo as string),
      );
    }
    if (dirtyFields.agency?.address && data.agency.address)
      formData.append('address', data.agency.address);
    if (dirtyFields.agency?.city && data.agency.city)
      formData.append('city', data.agency.city);
    if (dirtyFields.agency?.postalCode && data.agency.postalCode != null)
      formData.append('postalCode', data.agency.postalCode.toString());
    if (dirtyFields.agency?.country && data.agency.country?.value)
      formData.append('country', data.agency.country?.value);
    if (dirtyFields.agency?.website && data.agency.website !== undefined)
      formData.append('website', data.agency.website);
    const marginRateForApi = resolveMarginRateForApiUpdate(
      Boolean(dirtyFields.agency?.marginRate),
      data.agency.marginRate,
    );
    if (marginRateForApi !== undefined) {
      formData.append(
        'marginRate',
        marginRateForApi === null ? '' : String(marginRateForApi),
      );
    }
    if (dirtyFields.agency?.siren && data.agency.siren)
      formData.append('siren', data.agency.siren);
    if (dirtyFields.email) formData.append('email', data.email);
    if (dirtyFields.firstName) formData.append('firstname', data.firstName);
    if (dirtyFields.lastName) formData.append('lastname', data.lastName);
    if (dirtyFields.password && data.password)
      formData.append('password', data.password);
    if (dirtyFields.phoneNumber && data.phoneNumber)
      formData.append('phoneNumber', data.phoneNumber);

    if ([...formData.keys()].length === 0) {
      setIsEditEnabled(false);
      return;
    }

    await toastAsync(updateUserData(formData), {
      success: t('pages.profile.profileUpdatedSuccess'),
    });

    setIsEditEnabled(false);
  };

  const onInvalid: SubmitErrorHandler<DistributorProfileFormData> = (
    formErrors,
  ) => {
    const findMessage = (value: unknown): string | undefined => {
      if (
        value &&
        typeof value === 'object' &&
        'message' in value &&
        typeof (value as { message?: unknown }).message === 'string'
      ) {
        return (value as { message: string }).message;
      }
      if (value && typeof value === 'object') {
        for (const nested of Object.values(value)) {
          const message = findMessage(nested);
          if (message) return message;
        }
      }
      return undefined;
    };

    toast.error(
      findMessage(formErrors) ?? t('common.errorOccurred'),
      toastOptions,
    );
  };

  return (
    <StyledContainer>
      <StyledFormHeader
        title={t('pages.profile.myAccount')}
        subTitle={t('pages.profile.manageConnectionAndBilling')}
      >
        <StyledButton
          title={t('pages.profile.logout')}
          variant="outlined"
          className="font-semibold gap-1 px-3 py-2"
          onClick={() => {
            logout();
          }}
        />
        <form action={`mailto:${CONTACT_EMAIL}`} method="GET" target="_blank">
          <StyledButton
            title={t('pages.profile.contactSupport')}
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2"
            icon={<MailIcon className="w-5 h-5" fill="white" />}
          />
        </form>
      </StyledFormHeader>
      <ProfileAccountSelector currentUserId={userData.id} />
      <HorizontalLine />
      <Tabs defaultValue="profile">
        <div className="flex justify-between p-6">
          <TabsList>
            <TabsTrigger
              value="profile"
              className={clsx('hover:cursor-pointer', {
                hidden: !isAdmin,
              })}
              onClick={() => setCurrentTab('profile')}
            >
              {t('pages.profile.generalInfo')}
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger
                value="exchangeRates"
                className="hover:cursor-pointer"
                onClick={() => setCurrentTab('exchangeRates')}
              >
                {t('pages.profile.exchangeRates.tab')}
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger
                value="users"
                className="hover:cursor-pointer"
                onClick={() => setCurrentTab('users')}
              >
                {t('pages.profile.users')}
              </TabsTrigger>
            )}
          </TabsList>
          {currentTab !== 'users' && currentTab !== 'exchangeRates' ? (
            <>
              {isEditEnabled ? (
                <div className="flex flex-row gap-4">
                  <StyledButton
                    title={t('pages.profile.cancel')}
                    onClick={() => {
                      setIsEditEnabled(false);
                      reset();
                    }}
                    variant="outlined"
                    className="font-semibold gap-1 px-3 py-2"
                  />
                  <StyledButton
                    title={t('pages.profile.saveMyInfo')}
                    className="font-semibold gap-1 px-3 py-2"
                    icon={<SaveIcon className="w-5 h-5" />}
                    onClick={handleSubmit(submitForm, onInvalid)}
                  />
                </div>
              ) : (
                <StyledButton
                  title={t('pages.profile.editMyInfo')}
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<EditIcon className="w-5 h-5 stroke-white" />}
                  onClick={() => setIsEditEnabled(true)}
                />
              )}
            </>
          ) : (
            <div className="flex flex-row gap-4">
              <StyledButton
                title={t('pages.profile.delete')}
                onClick={() => {
                  setIsUserDeletionModalOpen(true);
                }}
                disabled={selectedUsersIds.length === 0}
                variant="outlined"
                className="font-semibold border-error-300 text-error-600 gap-1 px-3 py-2"
                icon={<TrashIcon className="w-5 h-5 stroke-error-600" />}
              />
              <StyledButton
                title={t('pages.profile.addUser')}
                className="font-semibold gap-1 px-3 py-2"
                icon={<PlusIcon className="size-5 fill-white" />}
                onClick={() => {
                  setIsAdditionModalOpen(true);
                }}
              />
            </div>
          )}
        </div>

        <TabsContent value="profile">
          <DistributorProfileGeneralTabForm
            control={control}
            isEditEnabled={isEditEnabled}
            isAdmin={isAdmin}
          />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="exchangeRates">
            <DistributorProfileExchangeRatesTab />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="users">
            <DistributorProfileEmployeesTab setSelected={setSelectedUsersIds} />
          </TabsContent>
        )}
      </Tabs>
      {isAdmin && isAdditionModalOpen && (
        <DistributorProfileEmployeeAdditionModal
          closeModal={() => {
            setIsAdditionModalOpen(false);
          }}
        />
      )}
      {isAdmin && isUserDeletionModalOpen && selectedUsersIds.length > 0 && (
        <ModalDeletion
          type="user"
          actionOnClick={() => {
            if (selectedUsersIds.length === 1)
              toastAsync(deleteEmployee(selectedUsersIds[0]), {
                success: t('pages.profile.userDeletedSuccess'),
              });
            else
              toastAsync(deleteMultipleEmployees(selectedUsersIds), {
                success: t('pages.profile.usersDeletedSuccess'),
              });
          }}
          closeModal={() => {
            setSelectedUsersIds([]);
            setIsUserDeletionModalOpen(false);
          }}
          isMultiple={selectedUsersIds.length > 1}
        />
      )}
    </StyledContainer>
  );
};

export default DistributorProfileForm;
